import api from './api';
import type {
  Airport,
  Route,
  CrewMember,
  Note,
  NoteListItem,
  NoteCreateRequest,
  PhaseNoteDraft,
  RouteGroup,
  AirportGroup,
  CrewGroup,
  NoteFilterOptions,
  PlazaNoteListItem,
  PlazaNoteDetail,
  PlazaNoteParams,
  NoteItem,
} from '@/types/notes.types';
import type { PaginatedResult } from './api';

// 机场 API
export const airportApi = {
  getAirports: (params?: { search?: string; simple?: boolean; page?: number; pageSize?: number }) =>
    api.get('/notes/airports/', { params }) as Promise<{ data: PaginatedResult<Airport> }>,

  getAirport: (iataCode: string) =>
    api.get(`/notes/airports/${iataCode}/`) as Promise<{ data: Airport }>,
};

// 航线 API
export const routeApi = {
  getRoutes: (params?: { departure?: string; arrival?: string; search?: string; page?: number; pageSize?: number }) =>
    api.get('/notes/routes/', { params }) as Promise<{ data: PaginatedResult<Route> }>,

  getRoute: (id: number) =>
    api.get(`/notes/routes/${id}/`) as Promise<{ data: Route }>,

  createFromCodes: (departure: string, arrival: string, name?: string) =>
    api.post('/notes/routes/create_from_codes/', { departure, arrival, name }) as Promise<{
      data: { success: boolean; data: Route; message: string };
    }>,
};

// 机组人员 API
export const crewApi = {
  getCrewMembers: (params?: { search?: string; role?: string; page?: number; pageSize?: number }) =>
    api.get('/notes/crew_members/', { params }) as Promise<{ data: PaginatedResult<CrewMember> }>,

  getCrewMember: (id: number) =>
    api.get(`/notes/crew_members/${id}/`) as Promise<{ data: CrewMember }>,
};

// 笔记 API
export const notesApi = {
  // 列表
  getNotes: (params?: {
    search?: string;
    semanticSearch?: string;
    flightLog?: number;
    flightOperation?: number;
    route?: number;
    airport?: string;
    crew?: number;
    tag?: string;
    entity?: number;
    category?: string;
    conditionTag?: string;
    itemPhase?: string;
    sortBy?: 'updatedAt' | 'createdAt' | 'title';
    page?: number;
    pageSize?: number;
  }) => {
    if (!params) return api.get('/notes/') as Promise<{ data: PaginatedResult<NoteListItem> }>;
    return api.get('/notes/', { params }) as Promise<{ data: PaginatedResult<NoteListItem> }>;
  },

  // 详情
  getNote: (id: number) =>
    api.get(`/notes/${id}/`) as Promise<{ data: { success: boolean; data: Note } }>,

  // 创建
  createNote: (payload: NoteCreateRequest) =>
    api.post('/notes/', payload) as Promise<{
      data: { success: boolean; data: Note; message: string };
    }>,

  // 更新
  updateNote: (id: number, payload: NoteCreateRequest) =>
    api.put(`/notes/${id}/`, payload) as Promise<{
      data: { success: boolean; data: Note; message: string };
    }>,

  reviewItem: (
    noteId: number,
    itemId: number,
    payload: {
      decision: 'confirm' | 'correct' | 'reject';
      correction?: Partial<Pick<NoteItem, 'category' | 'phase' | 'text' | 'conditionTags'>>;
    },
  ) =>
    api.post(`/notes/${noteId}/items/${itemId}/review/`, payload) as Promise<{
      data: { success: boolean; data: { item: NoteItem }; message: string };
    }>,

  // 批量创建
  batchCreateNotes: (payload: {
    flightLog?: number | null;
    flightLeg?: number | null;
    flightOperation?: number | null;
    notes: PhaseNoteDraft[];
  }) =>
    api.post('/notes/batch_create/', payload) as Promise<{
      data: {
        success: boolean;
        data: { createdIds: number[]; createdCount: number; errors: unknown[] };
        message: string;
      };
    }>,

  // 删除
  deleteNote: (id: number, options?: { hard?: boolean }) =>
    api.delete(`/notes/${id}/`, { params: options?.hard ? { hard: 'true' } : undefined }) as Promise<{
      data: { success: boolean; message: string };
    }>,

  // 置顶/取消置顶
  pinNote: (id: number) =>
    api.post(`/notes/${id}/pin/`) as Promise<{
      data: { success: boolean; data: { isPinned: boolean }; message: string };
    }>,

  // 筛选选项
  getFilterOptions: () =>
    api.get('/notes/filter_options/') as Promise<{
      data: { success: boolean; data: NoteFilterOptions };
    }>,

  // 按航线聚合
  getByRoute: () =>
    api.get('/notes/by_route/') as Promise<{
      data: { success: boolean; data: RouteGroup[] };
    }>,

  // 按机场聚合
  getByAirport: () =>
    api.get('/notes/by_airport/') as Promise<{
      data: { success: boolean; data: AirportGroup[] };
    }>,

  // 按机组聚合
  getByCrew: () =>
    api.get('/notes/by_crew/') as Promise<{
      data: { success: boolean; data: CrewGroup[] };
    }>,

  // 分享/取消分享
  shareToggle: (id: number) =>
    api.post(`/notes/${id}/share_toggle/`) as Promise<{
      data: { success: boolean; data: { isShared: boolean; isAnonymous: boolean }; message: string };
    }>,

  // 点赞/取消点赞
  likeNote: (id: number) =>
    api.post(`/notes/${id}/like/`) as Promise<{
      data: { success: boolean; data: { isLiked: boolean; likeCount: number }; message: string };
    }>,

  // 收藏/取消收藏
  favoriteNote: (id: number) =>
    api.post(`/notes/${id}/favorite/`) as Promise<{
      data: { success: boolean; data: { isFavorited: boolean; favoriteCount: number }; message: string };
    }>,
};

// 笔记广场 API
export const plazaApi = {
  getPlazaNotes: (params?: PlazaNoteParams) =>
    api.get('/notes/plaza/', { params }) as Promise<{ data: PaginatedResult<PlazaNoteListItem> }>,

  getPlazaNote: (id: number) =>
    api.get(`/notes/plaza/${id}/`) as Promise<{ data: { success: boolean; data: PlazaNoteDetail } }>,
};
